// Small presentational pieces colocated for the in-treatment session flow.
// Extracted from NewSession.jsx during Iter AN Phase 2 — visuals + testids preserved bit-for-bit.

import { ArrowLeft, ArrowRight, Cloud, CloudOff } from "lucide-react";
import VoiceTextarea from "../../components/VoiceTextarea";
import { Switch } from "../../components/ui/switch";
import HorizontalScroller from "../../components/ui/HorizontalScroller";
import StickyFooterCTA from "../../components/ui/StickyFooterCTA";
import {
  PRE_STEPS, TX_STEPS, EOT_STEPS, POST_STEPS,
  PRE_LABELS, TX_LABELS, EOT_LABELS, POST_LABELS, SUPER_FOR,
} from "./constants";


export function formatRelative(date) {
  const sec = Math.floor((Date.now() - date.getTime()) / 1000);
  if (sec < 5) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  return date.toLocaleTimeString();
}


export function SaveIndicator({ status, savedAt }) {
  if (status === "saving") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-paper-100 text-ink-700 text-xs" data-testid="save-indicator-saving">
        <span className="w-2 h-2 rounded-full border-2 border-sage-500 border-t-transparent animate-spin" />
        Saving…
      </span>
    );
  }
  if (status === "error") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-terracotta-500/10 text-terracotta-600 text-xs" data-testid="save-indicator-error">
        <CloudOff className="w-3 h-3" /> Save failed — retrying…
      </span>
    );
  }
  if (status === "saved") {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-sage-500/10 text-sage-700 text-xs" data-testid="save-indicator-saved">
        <Cloud className="w-3 h-3" />
        {savedAt ? `Draft saved · ${formatRelative(savedAt)}` : "Draft saved"}
      </span>
    );
  }
  return null;
}


// Super-phase pill bar: 4 chunks (Pre / Treatment / End-of-Treatment / Post)
export function SuperPhaseRibbon({ phase, onJump }) {
  const sup = SUPER_FOR(phase);
  const items = [
    { key: "pre", label: "PRE", first: "pre-setup" },
    { key: "tx", label: "TREATMENT", first: "tx-diagram" },
    { key: "eot", label: "REASSESS", first: "post-pain" },
    { key: "post", label: "WRAP-UP", first: "post-photos" },
  ];
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="super-phase-ribbon">
      {items.map((it, i) => (
        <button
          key={it.key}
          type="button"
          onClick={() => onJump(it.first)}
          data-testid={`super-phase-${it.key}`}
          className={`text-[11px] font-bold uppercase tracking-[0.14em] min-h-[36px] px-4 rounded-full transition-colors ${
            sup === it.key
              ? "bg-ink-900 text-white"
              : "bg-paper-100 text-ink-500 hover:bg-paper-200"
          }`}
        >
          {i + 1}. {it.label}
        </button>
      ))}
    </div>
  );
}


export function SubStepIndicator({ phase, onJump, photoMode, includeRom = true, hasPrevious = false, continuation = false }) {
  const sup = SUPER_FOR(phase);
  let group = [];
  let labels = {};
  let supLabel = "";
  let supColor = "";
  if (sup === "pre") {
    group = PRE_STEPS;
    if (!hasPrevious) group = group.filter((k) => k !== "pre-changes");
    if (continuation) group = group.filter((k) => k !== "pre-complaint");
    if (photoMode === "none") group = group.filter((k) => k !== "pre-photos");
    if (!includeRom) group = group.filter((k) => k !== "pre-rom");
    labels = PRE_LABELS;
    supLabel = "Pre-treatment";
    supColor = "text-sage-700";
  } else if (sup === "tx") {
    group = TX_STEPS;
    labels = TX_LABELS;
    supLabel = "Treatment";
    supColor = "text-ink-700";
  } else if (sup === "eot") {
    group = EOT_STEPS;
    if (!includeRom) group = group.filter((k) => k !== "post-rom");
    labels = EOT_LABELS;
    supLabel = "Reassess & findings";
    supColor = "text-amber-700";
  } else {
    group = POST_STEPS;
    labels = POST_LABELS;
    supLabel = "Reassessment";
    supColor = "text-terracotta-700";
  }
  const idx = group.indexOf(phase);
  return (
    <div className="flex items-center gap-3 mb-2" data-testid="sub-step-indicator">
      <span className={`text-[11px] font-bold uppercase tracking-[0.14em] ${supColor}`}>{supLabel}</span>
      <span className="text-xs text-ink-500">
        {idx >= 0 ? `${idx + 1} of ${group.length}` : ""} · {labels[phase] || ""}
      </span>
      <HorizontalScroller testid="sub-step-scroller" chevronTestidPrefix="sub-step" className="flex-1 min-w-0">
        {group.map((key, i) => (
          <button
            key={key}
            type="button"
            onClick={() => onJump(key)}
            data-testid={`sub-step-${key}`}
            className={`text-[11px] uppercase tracking-[0.12em] min-h-[28px] px-2.5 rounded-full whitespace-nowrap transition-colors shrink-0 ${
              phase === key
                ? "bg-ink-900 text-white"
                : "bg-paper-100 text-ink-500 hover:bg-paper-200"
            }`}
          >
            {i + 1}. {labels[key]}
          </button>
        ))}
      </HorizontalScroller>
    </div>
  );
}


export function PreStepWrapper({ title, subtitle, children, postPhase = false }) {
  return (
    <div data-testid={postPhase ? "post-step" : "pre-step"}>
      <h1 className="font-heading text-3xl sm:text-4xl font-light tracking-tight text-ink-900 mb-2">{title}</h1>
      {subtitle && <p className="text-ink-700 mb-6 max-w-2xl">{subtitle}</p>}
      <div className="space-y-6">{children}</div>
    </div>
  );
}


export function LinearNav({ onBack, onNext, nextLabel = "Continue", nextDisabled = false }) {
  return (
    <StickyFooterCTA testid="linear-nav" className="justify-between">
      <button
        type="button"
        onClick={onBack}
        className="min-h-[48px] px-5 rounded-full text-ink-700 hover:bg-paper-100 inline-flex items-center gap-2"
        data-testid="linear-back-btn"
      >
        <ArrowLeft className="w-4 h-4" /> Back
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={nextDisabled}
        className="min-h-[48px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2 disabled:opacity-50"
        data-testid="linear-next-btn"
      >
        <span className="whitespace-nowrap">{nextLabel}</span>
        <ArrowRight className="w-4 h-4" />
      </button>
    </StickyFooterCTA>
  );
}


// Opt-in toggle for ROM assessments — ROM steps are skipped entirely when off.
export function RomToggleCard({ checked, onChange, testId = "include-rom-toggle" }) {
  return (
    <div className="bg-white border border-paper-200 rounded-3xl p-6 flex items-center justify-between gap-4" data-testid={`${testId}-card`}>
      <div>
        <div className="font-medium text-ink-900">Include Range of Motion Assessment</div>
        <p className="text-sm text-ink-500 mt-1">Optional — turn on to record ROM measurements for this session.</p>
      </div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testId} />
    </div>
  );
}


// Treatment notes panel — primary surface for voice + text capture during table work.
export function TreatmentNotesPanel({ value, onChange, onSaveDraft }) {
  return (
    <div className="bg-white border border-paper-200 rounded-3xl p-6 sm:p-8" data-testid="tx-notes-panel">
      <div className="label-overline mb-2 text-sage-600">Treatment notes (voice or text)</div>
      <h2 className="font-heading text-xl text-ink-900 mb-2">What did you do on the table?</h2>
      <p className="text-sm text-ink-700 mb-4">
        Speak freely — the AI will use these notes to draft your SOAP, wrap-up, and post-treatment recommendations.
        You can keep editing here throughout the treatment.
      </p>
      <VoiceTextarea
        value={value}
        onChange={onChange}
        onBlur={() => onSaveDraft?.()}
        rows={8}
        placeholder='"Started supine — warmed left upper trap with effleurage, then trigger point work on levator. Cervical traction × 2 min. Side-lying scapular release on the right. Finished prone with thoracic mobilization."'
        testId="tx-notes-input"
      />
    </div>
  );
}
